/**
 * How an instalment reads on the parent "All installments" list (app and web).
 *
 * Every unpaid instalment used to carry the same "Due" badge, even one due next May, and
 * the list ran newest invoice first, so paid instalments sat below a run of future months
 * and looked missing (QA). Now each has one of five statuses and the list runs by due date.
 */
export type InstallmentState = 'Paid' | 'Partially paid' | 'Overdue' | 'Due' | 'Upcoming';

export const INSTALLMENT_STATE_COLORS: Record<InstallmentState, { bg: string; text: string }> = {
  Paid: { bg: '#DCFCE7', text: '#15803D' },
  'Partially paid': { bg: '#FFEDD5', text: '#C2410C' },
  Due: { bg: '#FEF9C3', text: '#A16207' },
  Overdue: { bg: '#FEE2E2', text: '#B91C1C' },
  Upcoming: { bg: '#DBEAFE', text: '#1D4ED8' },
};

/** Unpaid and due within this many days reads "Due"; later than that, "Upcoming". */
export const DUE_WINDOW_DAYS = 30;

interface InstallmentLike {
  status: string;
  amount: number;
  paidAmount: number;
  balanceAmount: number;
  dueDate: string;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function installmentState(inst: InstallmentLike, today: Date = new Date()): InstallmentState {
  if (inst.status === 'paid' || inst.balanceAmount <= 0) return 'Paid';
  if (inst.paidAmount > 0) return 'Partially paid';
  const due = new Date(inst.dueDate);
  if (isNaN(due.getTime())) return 'Due';
  const days = Math.round((startOfDay(due) - startOfDay(today)) / 86_400_000);
  if (days < 0) return 'Overdue';
  return days <= DUE_WINDOW_DAYS ? 'Due' : 'Upcoming';
}

/** By due date, earliest first; anything without a date goes last. */
export function byDueDate<T extends { dueDate: string }>(list: T[]): T[] {
  const time = (t: T) => {
    const ms = new Date(t.dueDate).getTime();
    return isNaN(ms) ? Number.MAX_SAFE_INTEGER : ms;
  };
  return [...list].sort((a, b) => time(a) - time(b));
}

/** How many instalments are in each state, in display order, leaving out empty ones. */
export function stateCounts<T extends InstallmentLike>(list: T[], today: Date = new Date()): { state: InstallmentState; count: number }[] {
  const order: InstallmentState[] = ['Paid', 'Partially paid', 'Overdue', 'Due', 'Upcoming'];
  const counts = new Map<InstallmentState, number>();
  for (const inst of list) {
    const s = installmentState(inst, today);
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  return order.filter(s => counts.has(s)).map(s => ({ state: s, count: counts.get(s)! }));
}

/** The instalments in one state, for a status chip the parent tapped; null means every one. */
export function inState<T extends InstallmentLike>(list: T[], state: InstallmentState | null, today: Date = new Date()): T[] {
  return state ? list.filter(inst => installmentState(inst, today) === state) : list;
}
